// smartTableTrace.ts
//
// Single source of truth for "what happened to the Smart Table grid, and why."
//
// This is a lightweight, always-on ring buffer (no sampling, no opt-in flag)
// that every grid-mutating code path and every cell-status transition writes
// to. It exists specifically to let us answer, with hard evidence instead of
// speculation, questions like:
//   - Did AG Grid reload blocks (remount renderers) or just patch cells?
//   - Which code path triggered that reload, and what triggered it?
//   - Did a cell's status flip, and which source flipped it?
//   - Did a filtered row get removed, and why?
//
// Usage (browser console):
//   window.__dumpSmartTableTrace__()                        // full log, formatted
//   window.__dumpSmartTableTrace__({ rowIndex: 12 })         // only row 12
//   window.__dumpSmartTableTrace__({ category: "renderer" }) // only renderer mounts
//   window.__smartTableTraceSummary__()                      // counts by op
//   window.__clearSmartTableTrace__()
//
// Renderer mount/unmount events are the ground truth for "did this remount":
// a `renderer-mount` for a row/column that was already loaded, appearing
// right after a `grid-op`, proves that grid-op destroyed and recreated the
// row node. `applyServerSideTransaction` / `refreshCells` never produce a
// remount; `refreshServerSide` and `redrawRows` always do for rows in the
// reloaded route.

export type SmartTableTraceCategory =
  | "grid-op" // applyServerSideTransaction, refreshServerSide, refreshCells, redrawRows, setRowCount
  | "cell-status" // any local cell status transition (optimistic, WS, rollback, stale-marking)
  | "renderer" // CellRenderer mount/unmount — ground truth for remount vs reuse
  | "ws" // parsed WebSocket messages
  | "filter"; // status-filter membership changes (row added/removed from filtered result)

export interface SmartTableTraceEvent {
  seq: number;
  /** Milliseconds since the first recorded event (relative, easier to scan). */
  tMs: number;
  category: SmartTableTraceCategory;
  /** Short operation name, e.g. "applyServerSideTransaction:update", "refreshServerSide", "renderer-mount". */
  op: string;
  /** Call site identifier, e.g. "sheet-data-store:_flushDirtyRows". */
  source: string;
  rowIndex?: number;
  columnId?: string;
  from?: string;
  to?: string;
  detail?: Record<string, unknown>;
}

interface DumpFilter {
  category?: SmartTableTraceCategory;
  rowIndex?: number;
  columnId?: string;
  op?: string;
  /** Only include events from the last N milliseconds. */
  sinceMs?: number;
  limit?: number;
}

declare global {
  interface Window {
    __SMART_TABLE_TRACE_DISABLED__?: boolean;
    __SMART_TABLE_TRACE_ENABLED__?: boolean;
    __dumpSmartTableTrace__?: (filter?: DumpFilter) => string;
    __dumpSmartTableTraceJSON__?: (filter?: DumpFilter) => SmartTableTraceEvent[];
    __clearSmartTableTrace__?: () => void;
    __smartTableTraceSummary__?: () => Record<string, number>;
  }
}

const MAX_EVENTS = 8_000;

let events: SmartTableTraceEvent[] = [];
let seq = 0;
let originMs: number | null = null;

export function traceSmartTable(entry: {
  category: SmartTableTraceCategory;
  op: string;
  source: string;
  rowIndex?: number;
  columnId?: string;
  from?: string;
  to?: string;
  detail?: Record<string, unknown>;
}): void {
  if (
    typeof window === "undefined" ||
    window.__SMART_TABLE_TRACE_DISABLED__ ||
    (!import.meta.env.DEV && window.__SMART_TABLE_TRACE_ENABLED__ !== true)
  ) {
    return;
  }

  const now = performance.now();
  if (originMs === null) {
    originMs = now;
  }

  events.push({
    ...entry,
    seq,
    tMs: Math.round(now - originMs),
  });
  seq += 1;

  if (events.length > MAX_EVENTS) {
    events.splice(0, events.length - MAX_EVENTS);
  }
}

/** Convenience helper for tracing every row affected by a batch grid-op in one call. */
export function traceSmartTableRows(entry: {
  category: SmartTableTraceCategory;
  op: string;
  source: string;
  rowIndices: Iterable<number>;
  detail?: Record<string, unknown>;
}): void {
  for (const rowIndex of entry.rowIndices) {
    traceSmartTable({
      category: entry.category,
      op: entry.op,
      source: entry.source,
      rowIndex,
      detail: entry.detail,
    });
  }
}

const matchesFilter = (event: SmartTableTraceEvent, filter?: DumpFilter): boolean => {
  if (!filter) {
    return true;
  }
  if (filter.category !== undefined && event.category !== filter.category) {
    return false;
  }
  if (filter.rowIndex !== undefined && event.rowIndex !== filter.rowIndex) {
    return false;
  }
  if (filter.columnId !== undefined && event.columnId !== filter.columnId) {
    return false;
  }
  if (filter.op !== undefined && event.op !== filter.op) {
    return false;
  }
  if (filter.sinceMs !== undefined) {
    const latestTMs = events.length > 0 ? events[events.length - 1].tMs : 0;
    if (latestTMs - event.tMs > filter.sinceMs) {
      return false;
    }
  }
  return true;
};

const formatEvent = (event: SmartTableTraceEvent): string => {
  const parts = [
    `#${event.seq}`,
    `+${event.tMs}ms`,
    `[${event.category}]`,
    event.op,
    `← ${event.source}`,
  ];
  if (event.rowIndex !== undefined) {
    parts.push(`row=${event.rowIndex}`);
  }
  if (event.columnId !== undefined) {
    parts.push(`col=${event.columnId}`);
  }
  if (event.from !== undefined || event.to !== undefined) {
    parts.push(`${event.from ?? "?"}→${event.to ?? "?"}`);
  }
  if (event.detail && Object.keys(event.detail).length > 0) {
    parts.push(JSON.stringify(event.detail));
  }
  return parts.join("  ");
};

const dumpFiltered = (filter?: DumpFilter): SmartTableTraceEvent[] => {
  const filtered = events.filter(event => matchesFilter(event, filter));
  return filter?.limit ? filtered.slice(-filter.limit) : filtered;
};

export function installSmartTableTraceTools(): void {
  if (
    typeof window === "undefined" ||
    (!import.meta.env.DEV && window.__SMART_TABLE_TRACE_ENABLED__ !== true)
  ) {
    return;
  }

  window.__dumpSmartTableTrace__ = (filter?: DumpFilter) =>
    dumpFiltered(filter).map(formatEvent).join("\n");

  window.__dumpSmartTableTraceJSON__ = (filter?: DumpFilter) => dumpFiltered(filter);

  window.__clearSmartTableTrace__ = () => {
    events = [];
    seq = 0;
    originMs = null;
  };

  window.__smartTableTraceSummary__ = () => {
    const counts: Record<string, number> = {};
    for (const event of events) {
      const key = `${event.category}:${event.op}`;
      counts[key] = (counts[key] ?? 0) + 1;
    }
    return counts;
  };
}

installSmartTableTraceTools();
